import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useActionState,
  useDeferredValue
} from 'react'
import { ModalBody, UncontrolledTooltip } from 'reactstrap'
import { IconSearch, IconFile } from '@tabler/icons-react'
import Skeleton from 'react-loading-skeleton'
//@ts-ignore
import { Button, Checkbox, InputGroup } from 'dsm-react-lib'
import { useDataboxService } from '../Teams/services/databoxService'
import { useThemeManager } from '../../../_hooks/useThemeManager'
import { getFileIcon } from './utils/helpers'
import SelectedSourcesPanel from '../Teams/modals/SelectedSourcesPanel'
import EmptyContent from '../Teams/EmptyContent'

interface PersonalDataboxTabProps {
  isOpen: boolean
  toggle: () => void
  // Shared tab bar, rendered as the first item of this tab's own left column so the right-hand
  tabsBar?: React.ReactNode
  // All databoxes already selected on the agent (Team Databoxes tab + this tab combined).
  // Only the entry tagged `scope: 'Personal'` belongs to this tab - everything else (the Team
  // Databoxes tab's own selections) is left untouched when this tab submits.
  existingDataboxes?: any[]
  addDataboxToAgent?: (databoxes: any) => void
  removeDataboxFromAgent?: (databoxIds: string[]) => void
  lockedFileIds?: string[]
  // Subset of lockedFileIds that belongs to the Personal Databox (lockedFileIds also holds team file ids)
  lockedPersonalFileIds?: string[]
  lockedDataboxIds?: string[]
  lockedDataProductIds?: string[]
  // The Team Databoxes tab's current (unsaved) selection, shown read-only in this tab's panel
  existingTeamDataboxesForPanel?: any[]
  onRemoveTeamDatabox?: (databoxId: string) => void
  onRemoveTeamDataboxItem?: (databoxId: string, itemId: string) => void
  // The Data Products tab's current (unsaved) selection, shown read-only in this tab's panel
  existingDataProductsForPanel?: Array<{ id: string; name: string }>
  onRemoveDataProduct?: (id: string) => void
  // Whether the agent had ANY data products persisted before this modal opened - unlike the live existingDataProductsForPanel, doesn't drop to 0 the instant the last one is unchecked, so the submit button stays enabled to persist that removal.
  hasExistingDataProducts?: boolean
  // Reports this tab's current (unsaved) selection to the parent modal
  onLiveSelectionChange?: (databoxes: any[]) => void
  // Reports whether the current user actually has a Personal Databox at all, so the parent
  // modal can hide this tab
  onPersonalDataboxAvailabilityChange?: (hasPersonalDatabox: boolean) => void
  // Reports whether this tab's own databox/file list fetch is still in flight
  onLoadingChange?: (isLoading: boolean) => void
  // Loading state of the OTHER two categories (this tab's own Personal state is computed locally)
  isFetchingTeam?: boolean
  isFetchingDataProducts?: boolean
  // True while the agent's existing data sources are still being fetched from the server -
  // shows a loading state in the Selected Sources panel instead of "No sources selected"
  isLoadingExistingSources?: boolean
  // Commits the Data Products tab's pending (unsaved) selection before this tab persists its own -
  // mirrors the Team Databoxes/Data Products tabs' own onBeforeSubmit so a data product removed
  // from this tab's panel is actually persisted, not just reflected live.
  onBeforeSubmit?: () => void
  // Ref exposing this tab's imperative handle to the parent DataSourcesModal
  ref?: React.Ref<PersonalDataboxTabHandle>
}

interface DataboxSummary {
  id: string
  name: string
  filesCount: number
}

// Imperative methods exposed to the parent DataSourcesModal, so the other tabs' panels can
// remove this tab's selections directly.
export interface PersonalDataboxTabHandle {
  clearSelection: () => void
  removeFile: (fileId: string) => void
}

// Resolve a stable document id from the various shapes the API may return.
const getDocId = (doc: any): string =>
  String(doc?.id ?? doc?.smartDocsDocumentId ?? doc?.externalId ?? '')

// Goes straight into the user's personal databox and lets them pick individual files
const PersonalDataboxTab = ({
  isOpen,
  toggle,
  tabsBar,
  existingDataboxes = [],
  addDataboxToAgent,
  removeDataboxFromAgent,
  lockedFileIds = [],
  lockedPersonalFileIds = [],
  lockedDataboxIds = [],
  lockedDataProductIds = [],
  existingTeamDataboxesForPanel = [],
  onRemoveTeamDatabox,
  onRemoveTeamDataboxItem,
  existingDataProductsForPanel = [],
  onRemoveDataProduct,
  hasExistingDataProducts = false,
  onLiveSelectionChange,
  onPersonalDataboxAvailabilityChange,
  onLoadingChange,
  isFetchingTeam,
  isFetchingDataProducts,
  isLoadingExistingSources = false,
  onBeforeSubmit,
  ref
}: PersonalDataboxTabProps) => {
  const { isDarkTheme: isDarkMode } = useThemeManager()
  const { getMyDataboxes, getDataboxDocumentsPaginated } = useDataboxService()

  const [databox, setDatabox] = useState<DataboxSummary | null>(null)
  const [isLoadingDatabox, setIsLoadingDatabox] = useState(false)
  const [documents, setDocuments] = useState<any[]>([])
  const [isLoadingDocs, setIsLoadingDocs] = useState(false)
  const [docSearch, setDocSearch] = useState('')

  // Selected files for the personal databox, keyed by file id.
  const [selectedFiles, setSelectedFiles] = useState<Record<string, string>>({})

  const existingPersonalDatabox = useMemo(
    () => existingDataboxes.find((db: any) => db.scope === 'Personal'),
    [existingDataboxes]
  )
  const lockedFileIdSet = useMemo(() => new Set(lockedFileIds.map(String)), [lockedFileIds])
  const lockedPersonalFileIdSet = useMemo(
    () => new Set(lockedPersonalFileIds.map(String)),
    [lockedPersonalFileIds]
  )
  const lockedDataboxIdSet = useMemo(
    () => new Set(lockedDataboxIds.map(String)),
    [lockedDataboxIds]
  )

  // Reset everything and load the personal databox + its files whenever the shared modal
  // (re)opens.
  useEffect(() => {
    if (!isOpen) return
    setDocSearch('')
    setDocuments([])
    setDatabox(null)

    const initialSelection: Record<string, string> = {}
    ;(existingPersonalDatabox?.selectedItems ?? []).forEach((item: any) => {
      initialSelection[String(item.id)] = item.name
    })
    // Locked agent files stay selected even if the thread's resolved selection lost them.
    lockedPersonalFileIdSet.forEach((id) => {
      if (!initialSelection[id]) initialSelection[id] = 'Untitled file'
    })
    setSelectedFiles(initialSelection)
    ;(async () => {
      setIsLoadingDatabox(true)
      const response = await getMyDataboxes()
      const items = response?.items ?? response ?? []
      const personalDatabox = (Array.isArray(items) ? items : []).find(
        (db: any) => String(db?.scope ?? '').toLowerCase() === 'personal'
      )
      setIsLoadingDatabox(false)
      if (!personalDatabox) {
        // No Personal Databox exists for this user - let the parent modal know so it can hide
        // this tab / move the active tab elsewhere instead of showing an empty one.
        onPersonalDataboxAvailabilityChange?.(false)
        return
      }
      onPersonalDataboxAvailabilityChange?.(true)

      const summary: DataboxSummary = {
        id: String(personalDatabox.id),
        name: personalDatabox.name ?? 'Personal databox',
        filesCount: personalDatabox.filesCount ?? personalDatabox.databoxDocumentsCount ?? 0
      }
      setDatabox(summary)

      setIsLoadingDocs(true)
      const docsResponse = await getDataboxDocumentsPaginated(
        summary.id,
        1,
        100,
        undefined,
        'uploadTime',
        'desc'
      )
      const docs = Array.isArray(docsResponse) ? docsResponse : []
      setDocuments(docs)
      setIsLoadingDocs(false)

      // The databox was already fully selected (no per-file narrowing recorded) - treat that as
      // "every file selected" now that the file list is known.
      const wasWholeDatabox =
        existingPersonalDatabox && !(existingPersonalDatabox.selectedItems?.length > 0)
      if (wasWholeDatabox && docs.length > 0) {
        const files: Record<string, string> = {}
        docs.forEach((doc) => {
          const docId = getDocId(doc)
          if (docId) files[docId] = doc?.name ?? 'Untitled file'
        })
        // Locked files outside the fetched page still have to survive.
        setSelectedFiles((prev) => {
          const next = { ...files }
          lockedPersonalFileIdSet.forEach((id) => {
            if (!next[id]) next[id] = prev[id] ?? 'Untitled file'
          })
          return next
        })
      } else {
        // Replace the placeholder names of locked files with their real names.
        setSelectedFiles((prev) => {
          const next = { ...prev }
          docs.forEach((doc) => {
            const docId = getDocId(doc)
            if (lockedPersonalFileIdSet.has(docId) && doc?.name) next[docId] = doc.name
          })
          return next
        })
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const toggleDocument = React.useCallback((doc: any) => {
    const docId = getDocId(doc)
    if (!docId) return
    setSelectedFiles((prev) => {
      const next = { ...prev }
      if (next[docId]) {
        delete next[docId]
      } else {
        next[docId] = doc?.name ?? 'Untitled file'
      }
      return next
    })
  }, [])

  // Remove a single selected file from the right-hand panel
  const removeSelectedFile = (_databoxId: string, fileId: string) => {
    if (lockedFileIdSet.has(String(fileId))) {
      return
    }
    setSelectedFiles((prev) => {
      const { [fileId]: _removed, ...rest } = prev
      return rest
    })
  }

  // Clear the whole selection from the right-hand panel (locked files stay)
  const clearAllSelectedFiles = () =>
    setSelectedFiles((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([id]) => lockedFileIdSet.has(String(id))))
    )

  const selectedCount = Object.keys(selectedFiles).length

  // Whether the shared "Selected sources" panel has anything in it at all - this tab's own
  // personal files/databox, or the Team Databoxes/Data Products tabs' current (unsaved)
  // selections - used to decide the footer button's label/enabled state instead of only
  // looking at this tab's own selectedCount.
  const hasAnySelectedSources =
    selectedCount > 0 ||
    !!existingPersonalDatabox ||
    existingTeamDataboxesForPanel.length > 0 ||
    existingDataProductsForPanel.length > 0 ||
    hasExistingDataProducts

  // Looks up a selected file's formatted size (from the loaded documents list) so the
  // resulting databox item can report a total selected size, same as team databoxes do.
  const getDocSizeFormatted = React.useCallback(
    (docId: string) => documents.find((doc) => getDocId(doc) === docId)?.sizeFormatted,
    [documents]
  )

  // Report this tab's current (not-yet-saved) selection to the parent, live, so the Team
  // Databoxes and Data Products tabs' panels can show it too. filesCount is the personal
  // databox's REAL total (from the API), not the selection count - lets the other tabs'
  // panels show "N files · 0 data products" the same way this tab does.
  useEffect(() => {
    if (!onLiveSelectionChange) return
    onLiveSelectionChange(
      databox
        ? [
            {
              id: databox.id,
              name: databox.name,
              filesCount: databox.filesCount,
              selectedItems: Object.entries(selectedFiles).map(([id, name]) => ({
                id,
                name,
                type: 'file',
                sizeFormatted: getDocSizeFormatted(id)
              }))
            }
          ]
        : []
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databox, selectedFiles, getDocSizeFormatted])

  // Reports this tab's own fetch state (loading its personal databox summary + file list) to the
  // parent modal, so it can be combined with the other tabs' loading state.
  useEffect(() => {
    onLoadingChange?.(isLoadingDatabox || isLoadingDocs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingDatabox, isLoadingDocs])

  useImperativeHandle(ref, () => ({
    clearSelection: clearAllSelectedFiles,
    removeFile: (fileId: string) => removeSelectedFile('', fileId)
  }))

  // Deferred so typing stays responsive - this list renders every row, unpaginated.
  const deferredDocSearch = useDeferredValue(docSearch)
  const filteredDocuments = useMemo(() => {
    const term = deferredDocSearch.trim().toLowerCase()
    const filtered = term
      ? documents.filter((doc) => (doc?.name ?? '').toLowerCase().includes(term))
      : documents
    // Latest uploaded files first
    return [...filtered].sort(
      (a, b) => new Date(b?.uploadedDate ?? 0).getTime() - new Date(a?.uploadedDate ?? 0).getTime()
    )
  }, [documents, deferredDocSearch])

  // Memoized so a sibling tab's selection change (which only affects this tab's read-only
  // "Selected sources" panel props) doesn't force React to rebuild/reconcile this entire file
  // list (can be 100+ rows) - a big perf win since all three DataSourcesModal tabs stay
  // mounted (just hidden) at the same time.
  const fileListContent = useMemo(() => {
    if (isLoadingDatabox || isLoadingDocs) {
      // Row-shaped skeleton (checkbox + icon + name) so the list doesn't jump once real files land
      return (
        <div className="py-1">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="flex items-center gap-1 p-1 border-b theme-frame-divider last:border-b-0">
              <Skeleton width={18} height={18} className="animated-skeleton" />
              <Skeleton circle height={20} width={20} className="animated-skeleton" />
              <div className="flex-1">
                <Skeleton
                  height={14}
                  width={`${60 + ((index * 7) % 30)}%`}
                  className="animated-skeleton"
                />
              </div>
            </div>
          ))}
        </div>
      )
    }
    if (!databox) {
      return (
        <div className="flex items-center justify-center py-8 theme-text-subheading text-body-default">
          No personal databox found.
        </div>
      )
    }
    if (documents.length === 0) {
      return (
        <div className="flex items-center justify-center py-5">
          <EmptyContent
            icon={<IconFile size={40} stroke={1.5} color="var(--color-primary)" />}
            title="No files added yet"
            description="Your personal databox is empty. Upload files to it to start querying them here."
            maxWidth="max-w-md"
          />
        </div>
      )
    }
    if (filteredDocuments.length === 0) {
      return (
        <div className="flex items-center justify-center py-8 theme-text-subheading text-body-default">
          No files found.
        </div>
      )
    }
    return filteredDocuments.map((doc, index) => {
      const docId = getDocId(doc)
      const isChecked = !!selectedFiles[docId]
      const isLocked = lockedFileIdSet.has(String(docId))
      const nameElementId = `personal-doc-name-${docId || index}`
      return (
        <label
          key={docId || `doc-${index}`}
          className={`flex items-center gap-1 p-1 border-b theme-frame-divider last:border-b-0 ${
            isLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer theme-hover-surface'
          }`}
          data-testid="personal-document-file">
          <Checkbox
            color="primary"
            variant="custom"
            checked={isChecked}
            disabled={isLocked}
            onChange={() => {
              if (!isLocked) toggleDocument(doc)
            }}
          />
          <span className="flex-shrink-0">{getFileIcon(doc?.name ?? '')}</span>
          <span
            id={nameElementId}
            className="flex-1 text-body-default theme-text-heading truncate"
            title={doc?.name}>
            {doc?.name ?? 'Untitled file'}
          </span>
          <UncontrolledTooltip target={nameElementId} placement="top">
            {doc?.name ?? 'Untitled file'}
          </UncontrolledTooltip>
        </label>
      )
    })
  }, [
    isLoadingDatabox,
    isLoadingDocs,
    databox,
    documents,
    filteredDocuments,
    selectedFiles,
    toggleDocument
  ])

  const [, submitAction, isSubmitting] = useActionState(async () => {
    // Commit the Data Products tab's pending selection too, so a product removed from this
    // tab's own panel gets persisted instead of only reflected in the live cross-tab state.
    onBeforeSubmit?.()

    // Use the LIVE team databox state
    const teamDataboxes = existingTeamDataboxesForPanel

    if (selectedCount === 0) {
      // No personal files selected - always drop the emptied Personal Databox explicitly, since addDataboxToAgent never removes entries that are simply absent from its list.
      if (existingPersonalDatabox && removeDataboxFromAgent) {
        // Same id preference (smartDocsDataboxId over id) as DataSourceSelect's removeDataboxFromAgent uses to match entries, or the removal silently no-ops.
        removeDataboxFromAgent([
          String(existingPersonalDatabox.smartDocsDataboxId || existingPersonalDatabox.id)
        ])
      }
      // Still persist any team databox edits from this tab's panel
      if (teamDataboxes.length > 0) {
        addDataboxToAgent?.(teamDataboxes)
      }
    } else {
      const newPersonalDatabox = {
        id: databox!.id,
        name: databox!.name,
        type: 'databox',
        scope: 'Personal',
        selectedItems: Object.entries(selectedFiles).map(([id, name]) => ({
          id,
          name,
          type: 'file',
          sizeFormatted: getDocSizeFormatted(id)
        }))
      }

      addDataboxToAgent?.([...teamDataboxes, newPersonalDatabox])
    }
    toggle()
    return null
  }, null)

  const handleSubmit = () => {
    if (isSubmitting || !databox) return
    submitAction()
  }
  // Reshape this tab's selection for the shared SelectedSourcesPanel - a single "databox" entry
  // (the personal databox) with the currently checked files as its narrowed selection - merged
  // with the Team Databoxes and Data Products tabs' current (unsaved) selections so all three
  // sources are always visible here too.
  // Falls back to the already-known existingPersonalDatabox (from props, available immediately)
  // while this tab's own network fetch (getMyDataboxes) is still in flight, so the panel doesn't
  // show a loader here while the Team Databoxes/Data Products tabs already show real content.
  const displayDatabox = databox ?? existingPersonalDatabox
  const ownPanelDatabox = displayDatabox
    ? [
        {
          id: displayDatabox.id,
          name: displayDatabox.name,
          databoxDocumentsCount: databox?.filesCount ?? selectedCount,
          filesCount: databox?.filesCount ?? selectedCount
        }
      ]
    : []
  const panelDataboxes = [...ownPanelDatabox, ...existingTeamDataboxesForPanel]

  const panelSelectedDocumentsByDatabox: Record<string, { name: string; items: any[] }> = {}
  const panelSelectedRows: string[] = []
  if (displayDatabox && selectedCount > 0) {
    panelSelectedDocumentsByDatabox[displayDatabox.id] = {
      name: displayDatabox.name,
      items: Object.entries(selectedFiles).map(([id, name]) => ({
        id,
        name,
        type: 'file',
        sizeFormatted: getDocSizeFormatted(id)
      }))
    }
  }
  existingTeamDataboxesForPanel.forEach((db: any) => {
    // Same id preference (smartDocsDataboxId over id) as AddDataboxModal uses internally, so remove/remove-all actually match its selectedRows.
    const key = db.smartDocsDataboxId || db.id
    if (db.selectedItems?.length > 0) {
      panelSelectedDocumentsByDatabox[key] = { name: db.name, items: db.selectedItems }
    } else {
      panelSelectedRows.push(key)
    }
  })

  return (
    <ModalBody
      className="flex flex-col flex-1 overflow-hidden p-1"
      data-testid="personal-databox-tab">
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - search + file list */}
        <div className="flex flex-col flex-1 overflow-hidden pr-2">
          {tabsBar}
          {/* Search */}
          <div className="mb-1">
            <InputGroup
              addon={<IconSearch size={14} />}
              addonPosition="start"
              addonType="merged"
              isDarkMode={isDarkMode}
              className="search-input theme-input-group w-full"
              placeholder="Search files"
              value={docSearch}
              onChange={(e: any) => setDocSearch(e.target.value)}
              data-testid="personal-databox-search"
            />
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto rounded" data-testid="personal-databox-list">
            {fileListContent}
          </div>
        </div>

        {/* Right panel - Selected Sources */}
        <div className="flex flex-col overflow-auto pl-3 ml-1 border-l theme-border-default w-[35%] max-h-[calc(100vh-200px)]">
          <SelectedSourcesPanel
            selectedRows={panelSelectedRows}
            selectedDocumentsByDatabox={panelSelectedDocumentsByDatabox}
            isFetchingPersonal={isLoadingExistingSources || isLoadingDatabox || isLoadingDocs}
            isFetchingTeam={isFetchingTeam}
            isFetchingDataProducts={isFetchingDataProducts}
            databoxes={panelDataboxes}
            databoxDetailsCache={{}}
            personalDataboxId={displayDatabox?.id}
            lockedDataboxIds={Array.from(lockedDataboxIdSet)}
            lockedItemIds={Array.from(lockedFileIdSet)}
            lockedDataProductIds={lockedDataProductIds}
            onRemoveDatabox={(databoxId) => {
              if (displayDatabox && String(databoxId) === String(displayDatabox.id)) {
                clearAllSelectedFiles()
                return
              }
              onRemoveTeamDatabox?.(databoxId)
            }}
            onRemoveItem={(databoxId, itemId) => {
              if (displayDatabox && String(databoxId) === String(displayDatabox.id)) {
                removeSelectedFile(databoxId, itemId)
                return
              }
              onRemoveTeamDataboxItem?.(databoxId, itemId)
            }}
            standaloneDataProducts={existingDataProductsForPanel}
            onRemoveDataProduct={onRemoveDataProduct}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 mt-2">
        <span
          className="text-body-default theme-text-subheading"
          data-testid="personal-databox-count">
          {selectedCount > 0
            ? `${selectedCount} file${selectedCount === 1 ? '' : 's'} selected`
            : 'No files selected'}
        </span>
        <div className="flex gap-1">
          <Button
            label="Cancel"
            variant="outline"
            className="theme-neutral-outline-btn"
            onClick={toggle}
            disabled={isSubmitting}
            data-testid="personal-databox-cancel"
          />
          <Button
            label={
              isSubmitting
                ? 'Updating...'
                : hasAnySelectedSources
                ? 'Update sources'
                : 'Add sources'
            }
            color="primary"
            disabled={isSubmitting || !hasAnySelectedSources}
            onClick={handleSubmit}
            data-testid="personal-databox-submit"
          />
        </div>
      </div>
    </ModalBody>
  )
}

PersonalDataboxTab.displayName = 'PersonalDataboxTab'

export default PersonalDataboxTab
